/**
 * Route demo (demo agencies only): addresses and attempts, nothing else. Type addresses, CivilHound puts them in
 * driving order from where you are, and each stop keeps its M · A · E · W circles as attempts are logged.
 * Stops and attempts are kept in this browser only (nothing is saved to the agency).
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { distanceFeet, getPaperType, OUTCOME_LABELS, planRoute, SC_PILOT_RULESET, windowTracker, type AttemptOutcome } from '@civilos/domain';
import { api } from '../shared/api.ts';
import { SlippyMap } from '../shared/SlippyMap.tsx';
import { ErrorBox } from '../shared/ui.tsx';
import { usePosition } from './position.ts';
import { NavBanner, useTurnByTurn } from './turnbyturn.tsx';

interface Attempt {
  id: string;
  at: string;
  outcome: AttemptOutcome;
}
interface Stop {
  id: string;
  address: string;
  matched: string;
  lat: number;
  lng: number;
  attempts: Attempt[];
  /** county = the county's address point (on the house); census = placed along the street by number (can be off); manual = moved by hand. */
  source?: 'county' | 'census' | 'manual';
  /** Muted: off the route until this local date (YYYY-MM-DD) has passed — "done for today". */
  mutedOn?: string;
}
type LatLng = { lat: number; lng: number };

const KEY = 'civilos.routeDemo';
const OUTCOMES: AttemptOutcome[] = ['no_answer', 'not_home', 'gated_no_access', 'other_unsuccessful', 'served', 'bad_address', 'moved', 'vacant'];
/** These end the paper: the stop comes off the route. */
const FINAL: AttemptOutcome[] = ['served', 'bad_address', 'moved', 'vacant', 'deceased', 'outside_service_area', 'unable_to_locate'];
const TYPE = getPaperType(SC_PILOT_RULESET, 'SUMMONS_COMPLAINT');
const rid = () => Math.random().toString(36).slice(2, 10);

function load(): Stop[] {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? '[]');
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}
function save(stops: Stop[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify(stops));
  } catch {
    /* storage unavailable: the demo still works until the page is closed */
  }
}
const isDone = (s: Stop) => s.attempts.some((a) => FINAL.includes(a.outcome));
/** Today's local date, YYYY-MM-DD. */
const today = () => localInput(new Date()).slice(0, 10);
const isMuted = (s: Stop, day: string) => s.mutedOn === day;
const when = (iso: string) => new Date(iso).toLocaleString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' });
/** Local "YYYY-MM-DDTHH:MM" for a datetime-local input. */
const localInput = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
/** Every stop in order as one Google Maps trip (Google takes up to 9 stops between start and finish). */
function wholeRouteUrl(stops: Stop[], from: LatLng | null): string {
  const ll = (p: LatLng) => `${p.lat},${p.lng}`;
  const list = stops.slice(0, 10);
  const dest = list[list.length - 1]!;
  const via = list.slice(0, -1);
  const q = new URLSearchParams({ api: '1', destination: ll(dest), travelmode: 'driving' });
  if (from) q.set('origin', ll(from));
  if (via.length) q.set('waypoints', via.map(ll).join('|'));
  return `https://www.google.com/maps/dir/?${q}`;
}
const navUrl = (s: Stop) => `https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lng}`;

function Circles({ stop }: { stop: Stop }) {
  const w = windowTracker(
    SC_PILOT_RULESET,
    TYPE,
    stop.attempts.map((a) => ({ id: a.id, attemptedAt: new Date(a.at), outcome: a.outcome })),
  );
  if (!w) return null;
  return (
    <span className="svc-line" aria-label={`${w.green} of ${w.slots.length} time windows filled; ${w.needed} needed for non-service`}>
      {w.slots.map((s) => (
        <span key={s.id} className={`svc-c ${s.attemptId ? 'on' : ''}`} title={s.attemptId && s.at ? `${s.label}: ${when(new Date(s.at).toISOString())}` : `${s.label}: not yet`}>
          {s.letter}
        </span>
      ))}
      {w.satisfied && <span className="svc-ok">Non-service ready</span>}
    </span>
  );
}

function AttemptForm({ onSave, onCancel }: { onSave: (a: Omit<Attempt, 'id'>, muteToday: boolean) => void; onCancel: () => void }) {
  const [outcome, setOutcome] = useState<AttemptOutcome>('no_answer');
  const [mute, setMute] = useState(false);
  const [at, setAt] = useState(localInput(new Date()));
  return (
    <div className="rd-attempt">
      <div className="rd-outcomes" role="radiogroup" aria-label="What happened">
        {OUTCOMES.map((o) => (
          <button key={o} type="button" role="radio" aria-checked={outcome === o} className={`btn sm ${outcome === o ? 'primary' : ''}`} onClick={() => setOutcome(o)}>
            {OUTCOME_LABELS[o]}
          </button>
        ))}
      </div>
      <div className="row gap-sm wrap" style={{ alignItems: 'end' }}>
        <label className="rd-when">
          When
          <input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} />
        </label>
        {!FINAL.includes(outcome) && (
          <label className="check rd-mute-check">
            <input type="checkbox" checked={mute} onChange={(e) => setMute(e.target.checked)} /> Mute for the rest of today
          </label>
        )}
        <button type="button" className="btn primary" disabled={!at} onClick={() => onSave({ outcome, at: new Date(at).toISOString() }, mute && !FINAL.includes(outcome))}>
          Save attempt
        </button>
        <button type="button" className="btn ghost" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export function RouteDemo() {
  const [stops, setStops] = useState<Stop[]>(load);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [showDone, setShowDone] = useState(false);
  const [showMuted, setShowMuted] = useState(false);
  // Live position (the car on the map turns with the direction of travel). The route is planned from where you
  // were when the stops last changed, so the order doesn't reshuffle while you drive.
  const { pos, err: gpsErr } = usePosition();
  const [startAt, setStartAt] = useState<LatLng | null>(null);
  // Directions inside CivilHound: which stop you're driving to, and the map's cue to center on your car.
  const tbt = useTurnByTurn(pos);
  const [navStop, setNavStop] = useState<string | null>(null);
  const [followSignal, setFollowSignal] = useState(0);
  const startNav = (s: Stop) => {
    setNavStop(s.id);
    setOpen(null);
    tbt.start({ lat: s.lat, lng: s.lng }, s.address);
    setFollowSignal((n) => n + 1);
  };
  const endNav = () => (tbt.stop(), setNavStop(null));
  const me = pos;
  const gpsNote = !pos ? (gpsErr ? `${gpsErr}. The route starts at the first address.` : 'Finding you…') : null;

  useEffect(() => save(stops), [stops]);
  // Stops placed before the county's address points were loaded (or by the Census): look them up again once,
  // so they move onto the house.
  useEffect(() => {
    const stale = load().filter((x) => x.source !== 'county' && x.source !== 'manual');
    if (!stale.length) return;
    let alive = true;
    (async () => {
      for (const x of stale) {
        try {
          const r = await api.post<{ lat: number; lng: number; matchedAddress: string; source: 'county' | 'census' }>('/api/route-demo/locate', { address: x.address });
          if (!alive) return;
          setStops((all) => all.map((y) => (y.id === x.id ? { ...y, lat: r.lat, lng: r.lng, matched: r.matchedAddress, source: r.source } : y)));
        } catch {
          /* keep where it was */
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, []);
  // Move a pin by hand: tap "Move pin", then tap the house on the map.
  const [moving, setMoving] = useState<string | null>(null);
  const placePin = (p: LatLng) => {
    if (!moving) return;
    setStops((all) => all.map((y) => (y.id === moving ? { ...y, lat: p.lat, lng: p.lng, source: 'manual' } : y)));
    setMoving(null);
  };
  useEffect(() => {
    if (!pos) return;
    // Re-sort by what's closest as you drive: a new order from where you are once you've gone a quarter mile.
    // Not while directions are on, so the stop you're driving to doesn't change under you.
    if (!startAt || (!tbt.nav && distanceFeet(startAt, pos) > 1320)) setStartAt({ lat: pos.lat, lng: pos.lng });
  }, [pos, startAt, tbt.nav]);
  const activeKey = stops.filter((s) => !isDone(s) && !isMuted(s, today())).map((s) => s.id).join(',');
  useEffect(() => {
    if (pos) setStartAt({ lat: pos.lat, lng: pos.lng });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey]);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    const lines = text.split(/\n+/).map((s) => s.trim()).filter(Boolean);
    if (!lines.length) return;
    setBusy(true);
    setError(null);
    const missed: string[] = [];
    const found: Stop[] = [];
    for (const line of lines) {
      try {
        const r = await api.post<{ lat: number; lng: number; matchedAddress: string; source: 'county' | 'census' }>('/api/route-demo/locate', { address: line });
        found.push({ id: rid(), address: line, matched: r.matchedAddress, lat: r.lat, lng: r.lng, attempts: [], source: r.source });
      } catch (err) {
        missed.push(line);
        if (lines.length === 1) setError(err);
      }
    }
    setStops((s) => [...s, ...found]);
    setText(missed.join('\n'));
    if (missed.length && lines.length > 1) setError(new Error(`Couldn’t find ${missed.length === 1 ? 'this address' : 'these addresses'} (left in the box). Add the city and ZIP and try again.`));
    setBusy(false);
  };

  // The day rolls over at midnight: muted stops come back on their own.
  const [day, setDay] = useState(today);
  useEffect(() => {
    const t = setInterval(() => setDay(today()), 60_000);
    return () => clearInterval(t);
  }, []);
  const active = stops.filter((s) => !isDone(s) && !isMuted(s, day));
  const muted = stops.filter((s) => !isDone(s) && isMuted(s, day));
  const done = stops.filter(isDone);
  const plan = useMemo(() => {
    if (!active.length) return null;
    const start = startAt ?? active[0]!;
    const r = planRoute(active.map((s) => ({ id: s.id, lat: s.lat, lng: s.lng, addressKey: `${s.lat.toFixed(5)},${s.lng.toFixed(5)}` })), start, null);
    const order = r.stops.flatMap((g) => g.paperIds);
    return { order, miles: r.totalMiles, line: [start, ...r.stops.map((g) => ({ lat: g.lat, lng: g.lng }))] };
  }, [active.map((s) => `${s.id}${s.lat}${s.lng}`).join('|'), startAt?.lat, startAt?.lng]);
  const ordered = plan ? plan.order.map((id) => active.find((s) => s.id === id)!).filter(Boolean) : [];
  const markers = ordered.map((s, i) => ({ id: s.id, lat: s.lat, lng: s.lng, color: i === 0 ? '#1d4ed8' : '#475467', label: String(i + 1), title: `${i + 1}. ${s.address}`, selected: open === s.id, diamond: true }));

  const logAttempt = (id: string, a: Omit<Attempt, 'id'>, muteToday = false) => {
    setStops((all) => all.map((s) => (s.id === id ? { ...s, attempts: [...s.attempts, { ...a, id: rid() }].sort((x, y) => x.at.localeCompare(y.at)), ...(muteToday ? { mutedOn: today() } : {}) } : s)));
    setOpen(null);
  };
  const removeAttempt = (id: string, aid: string) => setStops((all) => all.map((s) => (s.id === id ? { ...s, attempts: s.attempts.filter((a) => a.id !== aid) } : s)));
  const setMuted = (id: string, on: boolean) => setStops((all) => all.map((s) => (s.id === id ? { ...s, mutedOn: on ? today() : undefined } : s)));
  const removeStop = (id: string) => setStops((all) => all.filter((s) => s.id !== id));
  const clearAll = () => {
    if (confirm('Clear every address and attempt from the route demo?')) setStops([]);
  };

  const stopCard = (s: Stop, n: number | null) => (
    <li key={s.id} className={`card rd-stop ${open === s.id ? 'open' : ''}`}>
      <div className="rd-head">
        {n != null ? <span className="rd-num">{n}</span> : isDone(s) ? <span className="rd-num done">✓</span> : <span className="rd-num muted" title="Muted for today">–</span>}
        <div className="rd-addr">
          <strong>{s.address}</strong>
          {s.matched && s.matched.toLowerCase() !== s.address.toLowerCase() && <div className="small muted">{s.matched}</div>}
          <div className="rd-src small">
            {s.source === 'county' ? (
              <span className="rd-tag ok">On the house</span>
            ) : s.source === 'manual' ? (
              <span className="rd-tag ok">Pin placed by hand</span>
            ) : (
              <span className="rd-tag warn" title="Placed along the street by house number — can be a few hundred feet off">Approximate</span>
            )}{' '}
            <button type="button" className="btn link small" onClick={() => setMoving(moving === s.id ? null : s.id)}>
              {moving === s.id ? 'Cancel move' : 'Move pin'}
            </button>
          </div>
          <Circles stop={s} />
        </div>
        <span className="rd-x">
          {!isDone(s) && (
            <button type="button" className="btn link small" aria-label={isMuted(s, day) ? `Unmute ${s.address}` : `Mute ${s.address} for the rest of today`} onClick={() => setMuted(s.id, !isMuted(s, day))}>
              {isMuted(s, day) ? 'Unmute' : 'Mute today'}
            </button>
          )}
          <button type="button" className="btn link small" aria-label={`Remove ${s.address}`} onClick={() => removeStop(s.id)}>
            Remove
          </button>
        </span>
      </div>
      {s.attempts.length > 0 && (
        <ul className="rd-attempts small">
          {s.attempts.map((a) => (
            <li key={a.id}>
              {when(a.at)} · <strong>{OUTCOME_LABELS[a.outcome]}</strong>{' '}
              <button type="button" className="btn link small" aria-label="Delete this attempt" onClick={() => removeAttempt(s.id, a.id)}>
                delete
              </button>
            </li>
          ))}
        </ul>
      )}
      {open === s.id ? (
        <AttemptForm onSave={(a, m) => logAttempt(s.id, a, m)} onCancel={() => setOpen(null)} />
      ) : (
        n != null && (
          <div className="row gap-sm wrap rd-actions">
            <button type="button" className={`btn ${navStop === s.id ? 'primary' : ''}`} onClick={() => (navStop === s.id ? endNav() : startNav(s))}>
              {navStop === s.id ? 'End directions' : 'Navigate'}
            </button>
            <button type="button" className="btn primary" onClick={() => setOpen(s.id)}>
              Attempted
            </button>
            <a className="btn link small" href={navUrl(s)} target="_blank" rel="noreferrer">
              Google Maps
            </a>
          </div>
        )
      )}
    </li>
  );

  return (
    <div className="rd-page">
      <header className="rd-top">
        <div>
          <h1>Route demo</h1>
          <div className="small muted">Addresses only. They go in driving order{me ? ' from where you are' : ''}.</div>
        </div>
        <div className="row gap-sm wrap">
          {ordered.length > 0 && (
            <a className="btn sm ghost" href={wholeRouteUrl(ordered, me)} target="_blank" rel="noreferrer" title="Every stop, in this order, as one trip in Google Maps">
              Whole route in Google Maps
            </a>
          )}
          {stops.length > 0 && (
            <button type="button" className="btn sm ghost" onClick={clearAll}>
              Clear all
            </button>
          )}
          <a className="btn sm ghost" href={location.pathname.startsWith('/field') ? '/field' : '/'}>
            Exit
          </a>
        </div>
      </header>
      <div className="rd-body">
        <section className="rd-left">
          <form className="card stack rd-add" onSubmit={add}>
            <label>
              Address
              <textarea
                rows={2}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) (e.preventDefault(), e.currentTarget.form?.requestSubmit());
                }}
                placeholder="123 Main St, Chester, SC 29706  (paste several, one per line)"
              />
            </label>
            <div className="row gap-sm">
              <button className="btn primary" disabled={busy || !text.trim()}>
                {busy ? 'Finding…' : 'Add to route'}
              </button>
            </div>
            <ErrorBox error={error} />
            {gpsNote && <div className="small muted">{gpsNote}</div>}
          </form>
          {plan && (
            <div className="small muted rd-miles">
              {ordered.length} {ordered.length === 1 ? 'stop' : 'stops'} · about {plan.miles} mi straight-line{pos ? ' · closest first from where you are' : ''}
              {pos && (
                <>
                  {' '}
                  <button type="button" className="btn link small" onClick={() => setStartAt({ lat: pos.lat, lng: pos.lng })}>
                    Re-sort from here
                  </button>
                </>
              )}
            </div>
          )}
          <ol className="rd-list">{ordered.map((s, i) => stopCard(s, i + 1))}</ol>
          {!stops.length && <p className="muted rd-empty">Add a few addresses to see the route.</p>}
          {muted.length > 0 && (
            <div className="rd-done">
              <button type="button" className="btn link small" onClick={() => setShowMuted(!showMuted)}>
                {showMuted ? 'Hide' : 'Show'} muted for today ({muted.length})
              </button>
              {showMuted && <ol className="rd-list">{muted.map((s) => stopCard(s, null))}</ol>}
            </div>
          )}
          {done.length > 0 && (
            <div className="rd-done">
              <button type="button" className="btn link small" onClick={() => setShowDone(!showDone)}>
                {showDone ? 'Hide' : 'Show'} finished ({done.length})
              </button>
              {showDone && <ol className="rd-list">{done.map((s) => stopCard(s, null))}</ol>}
            </div>
          )}
        </section>
        <section className="rd-map card">
          {tbt.nav && (
            <NavBanner
              nav={tbt.nav}
              muted={tbt.muted}
              onMute={() => tbt.setMuted(!tbt.muted)}
              onEnd={endNav}
              onAttempt={() => (setOpen(navStop), endNav())}
              googleUrl={`https://www.google.com/maps/dir/?api=1&destination=${tbt.nav.to.lat},${tbt.nav.to.lng}`}
            />
          )}
          {moving && <div className="rd-moving">Tap the house on the map to move the pin for {stops.find((x) => x.id === moving)?.address}.</div>}
          <SlippyMap
            height="100%"
            markers={markers}
            route={tbt.nav?.route?.path ?? plan?.line}
            me={me}
            fitKey={markers.map((m) => m.id).join(',')}
            followSignal={followSignal}
            onMarkerClick={moving ? undefined : (id) => setOpen(id)}
            onMapClick={moving ? placePin : undefined}
            crosshair={!!moving}
            ariaLabel="Route map"
          />
        </section>
      </div>
    </div>
  );
}
